import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";

import StepAnimator from "./components/detailComponents/StepAnimator";
import useNativeAuthBridge from "../../hooks/Regist/useNativeAuthBridge";
import { sendToApp, addAppMessageListener, notifyWebLoadingDone } from "../../bridges/appBridge";
import useAuth from "../../hooks/Regist/useAuth";
import { HeaderIconLActionR } from "../../assets/components/HeaderGroup";
import back from "../../assets/icons/Arrow/left.svg"
import close from "../../assets/icons/System/close.svg"

import Step0 from "./components/AdsRegist0";
import Step1 from "./components/AdsRegist1";
import Step2 from "./components/AdsRegist2";
import Step3 from "./components/AdsRegist3";
import ConciergeAskStep from "./components/AdsRegistConciergeAsk";
import ConciergeVerifyStep from "./components/AdsRegistConciergeVerify";
import ConciergeFindPinStep from "./components/AdsRegistConciergeFindPin";
import ConciergeConfirmStep from "./components/AdsRegistConciergeConfirm";
import Step4 from "./components/AdsRegist4";
import Step5 from "./components/AdsRegist5";
import Step6 from "./components/AdsRegist6";
import Step7 from "./components/AdsRegist7";
import Step8 from "./components/AdsRegist8";

export default function AdsRegister() {
  const navigate = useNavigate();
  const location = useLocation();

  const { session, isAuthed, loading, error } = useAuth();

  const [registerTag, setRegisterTag] = useState("");
  const [installationId, setInstallationId] = useState("");
  const [, setIsNextEnabled] = useState(false);
  const [step1InitialMode, setStep1InitialMode] = useState(null);
  const [hasPassedStep1, setHasPassedStep1] = useState(false);
  const [step5Items, setStep5Items] = useState([]);     // [{STORE_NAME, ROAD_NAME, ...}, ...]
  const [step6Shop, setStep6Shop] = useState(null);
  const [storeBusinessNumber, setStoreBusinessNumber] = useState("")
  const [step7Seed, setStep7Seed] = useState(null);   // {storeName, zonecode, roadAddress, detailAddress}
  const [step8Shop, setStep8Shop] = useState(null);
  const [conciergePreview, setConciergePreview] = useState(null);
  const [conciergePhone, setConciergePhone] = useState("");
  const [conciergePin, setConciergePin] = useState("");

  // step5 -> AdsSupport에서 돌아올 때 step5용 데이터 복원
  useEffect(() => {
    // origin이 step5가 아닐 땐 무시
    if (location.state?.origin !== "step5") return;

    const { items, seed } = location.state;

    if (Array.isArray(items) && items.length > 0) {
      setStep5Items(items);
    }
    if (seed) {
      setStep7Seed(seed);
    }
  }, [location.state]);

  // 네이티브 브리지 훅 (콜백은 여기서 바인딩)
  // useNativeAuthBridge({ installationId });
  const { isOK, resetAuthOK } = useNativeAuthBridge({ installationId });

  const getInitialStep = () => {
    try {
      const raw = new URLSearchParams(window.location.search).get('step');
      if (!raw) return 0;
      if (/^\d+$/.test(raw)) return Number(raw); // 숫자면 그대로
      // 별칭(step1/self4 등)이 오면 아래 alias 매핑 이후에 보정됨
      return 0;
    } catch { return 0; }
  };

  const [stepIndex, setStepIndex] = useState(getInitialStep);
  const [prevStepIndex, setPrevStepIndex] = useState(null); // Step7용 이전 step 추적

  // 네이티브/토큰 초기 부트스트랩
  useEffect(() => {
    // 1) 먼저 리스너 붙임 
    const off = addAppMessageListener((msg) => {
      if (msg?.type === 'PUSH_TOKEN' || (msg?.type === 'PERMISSION_STATUS' && msg?.payload?.token)) {
        setInstallationId(msg.payload?.install_id || "");

      }
    });
    // 2) 네이티브에 "리스너 준비됨" 알림 → 토큰 재전송 유도 
    sendToApp("WEB_READY", { ts: Date.now() });
    // 3) 이제 부트스트랩/라우팅 
    (async () => {
      try {
        const jwtFromNative = window.Android?.getToken?.();
        const jwtFromWeb = localStorage.getItem("access_token");
        const jwt = jwtFromNative || jwtFromWeb;
        if (jwt) axios.defaults.headers.common["Authorization"] = `Bearer ${jwt}`;
        // await resumeAndRoute();
      } catch {
        setStepIndex(0);
      } finally {
        notifyWebLoadingDone("AdsRegister");
      }
    })();
    return () => off?.();
  }, []);



  const goToMain = () => {
    if (storeBusinessNumber) {
      localStorage.setItem("store_business_number", storeBusinessNumber)
      navigate("/ads/main", { replace: true, state: { storeBusinessNumber } })
    } else {
      navigate("/ads/main", {
        replace: true,
        state: { storeBusinessNumber: localStorage.getItem("store_business_number") }
      });
    }
  };

  const goNext = () => {
    setStepIndex((prev) => {
      const next = Math.min(prev + 1, steps.length - 1);
      setPrevStepIndex(prev); //  “어디서 왔는지” 저장

      // Step1(1번 인덱스)에서 Step2(2번 인덱스)로 갈 때 플래그 켜기
      if (prev === indexOf(Step1) && next === indexOf(Step2)) {
        setHasPassedStep1(true);
      }

      return next;
    });
  };

  const goToStep = (idx) => {
    setStepIndex((current) => {
      const target = Math.max(0, Math.min(idx, steps.length - 1));
      if (current !== target) {
        setPrevStepIndex(current);  //  “어디서 왔는지” 저장
      }
      return target;
    });
  };

  // steps 정의
  // Step 추가 방법
  // { Component: Self3 },  
  // 원하는 위치에 임포트 후 추가
  const steps = [
    {
      Component: Step0,
      props: {
        setStep1InitialMode,
      },
    },
    {
      Component: Step1,
      props: {
        isOK,
        resetAuthOK,
        session: { session },
        isAuthed: { isAuthed },
        loading: { loading },
        error: { error },
        initialMode: step1InitialMode,
        allowAutoNext: !hasPassedStep1,
      }
    },
    { Component: Step2, props: { setIsNextEnabled, targetStep4: null } },
    { Component: Step3, props: { targetStore1: null } },
    {
      Component: ConciergeAskStep,
      props: {
        targetConciergeVerify: null,
        targetStoreSearch: null,
      },
    },
    {
      Component: ConciergeVerifyStep,
      props: {
        phone: conciergePhone,
        setPhone: setConciergePhone,
        pin: conciergePin,
        setPin: setConciergePin,
        setConciergePreview,
        targetConciergeConfirm: null,
        targetConciergeFindPin: null,
      },
    },
    {
      Component: ConciergeFindPinStep,
      props: {
        phone: conciergePhone,
        setPhone: setConciergePhone,
        pin: conciergePin,
        setPin: setConciergePin,
        targetConciergeVerify: null,
      },
    },
    {
      Component: ConciergeConfirmStep,
      props: {
        preview: conciergePreview,
        setStep6Shop,
        targetStoreRegister: null,
      },
    },
    {
      Component: Step4,
      props: {
        setStep5Items,
        setStep7Seed,
        targetStep5: null,
        targetStep7: null
      },
    },
    {
      Component: Step5,
      props: {
        items: step5Items,
        seed: step7Seed,
        setStep6Shop,
        targetStep6: null,
        targetStep7: null,
      },
    },
    {
      Component: Step6,
      props: {
        registerTag,
        setRegisterTag,
        setStoreBusinessNumber,
        setStep8Shop,
        shop: step6Shop,
        targetStep4: null,
        targetStep8: null,
      }
    },
    {
      Component: Step7,
      props: {
        setStoreBusinessNumber,
        setStep8Shop,
        seed: step7Seed,
        // targetStep8: null,
      }
    },
    // { Component: Step8 },
    {
      Component: Step8,
      props: {
        goToMain,
        shop: step8Shop,
        storeBusinessNumber,
      }
    },
  ];

  // 각 하위페이지의 index 조회
  const indexOf = (Cmp) => steps.findIndex((s) => s.Component === Cmp);

  // goToStep(target)의 target에 인덱스 주입
  // 각 step에서 다른 step들의 idx를 계산할 필요 없이 여기서 idx를 계산하여 이동을 시작할 페이지로 전달 (steps 선언 후에 안전하게 주입) 
  steps[indexOf(Step2)].props.targetStep4 = indexOf(ConciergeAskStep);
  steps[indexOf(ConciergeAskStep)].props.targetConciergeVerify = indexOf(ConciergeVerifyStep);
  steps[indexOf(ConciergeAskStep)].props.targetStoreSearch = indexOf(Step4);
  steps[indexOf(ConciergeVerifyStep)].props.targetConciergeFindPin = indexOf(ConciergeFindPinStep);
  steps[indexOf(ConciergeVerifyStep)].props.targetConciergeConfirm = indexOf(ConciergeConfirmStep);
  steps[indexOf(ConciergeFindPinStep)].props.targetConciergeVerify = indexOf(ConciergeVerifyStep);
  steps[indexOf(ConciergeConfirmStep)].props.targetStoreRegister = indexOf(Step6);
  // steps[indexOf(Step3)].props.targetStore1 = indexOf(Store1);
  steps[indexOf(Step4)].props.targetStep5 = indexOf(Step5);
  steps[indexOf(Step5)].props.targetStep6 = indexOf(Step6);
  steps[indexOf(Step4)].props.targetStep7 = indexOf(Step7);
  steps[indexOf(Step5)].props.targetStep7 = indexOf(Step7);
  steps[indexOf(Step6)].props.targetStep8 = indexOf(Step8);
  steps[indexOf(Step7)].props.targetStep8 = indexOf(Step8); // step7의 prop인 targetStep8에 Step8의 idx 주입 => goToStep(indexOf(Step8))하는 것과 동일
  // steps[indexOf(Step6)].props.targetStep4 = indexOf(Step4);
  // steps[indexOf(Store1)].props.targetStore2 = indexOf(Store2);
  // 필요하다면 Self3로 점프시키는 타깃도 주입
  // 예) Step2 → Self3로 보내고 싶다면:
  // steps[indexOf(Step2)].props.targetSelf3 = indexOf(Self3);

  // 별칭 → 인덱스 매핑
  // self3: indexOf(Self3),  별칭 추가하기
  const aliasToIndex = useMemo(
    () => ({
      step0: 0,
      // self0: indexOf(Self0),
      step1: indexOf(Step1),
      step2: indexOf(Step2),
      step3: indexOf(Step3),
      step4: indexOf(ConciergeAskStep),
      step5: indexOf(ConciergeVerifyStep),
      step6: indexOf(ConciergeFindPinStep),
      step7: indexOf(ConciergeConfirmStep),
      step8: indexOf(Step4),
      step9: indexOf(Step5),
      step10: indexOf(Step6),
      step11: indexOf(Step7),
      step12: indexOf(Step8),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [step5Items, step6Shop, registerTag, conciergePreview, conciergePhone, conciergePin] // 실질적으로 steps는 재생성되지 않지만, 안전을 위해 최소 deps
  );

  useEffect(() => {
    const curr = new URLSearchParams(window.location.search).get('step');
    const currStr = curr == null ? null : String(curr);
    const nextStr = String(stepIndex);
    if (currStr !== nextStr) {
      const next = new URLSearchParams(window.location.search);
      next.set('step', nextStr);
    }
  }, [stepIndex]);

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const raw = search.get("step");
    if (!raw) return;

    // 1) 숫자 step 처리 (?step=3)
    if (/^\d+$/.test(raw)) {
      const n = Number(raw);
      const clamped = Math.max(0, Math.min(n, steps.length - 1));
      setStepIndex(clamped);
      return;
    }

    // 2) 문자열 별칭 처리 (step3, step4 같은 거)
    const key = String(raw).toLowerCase();
    const idx = aliasToIndex[key];
    if (Number.isInteger(idx)) {
      setStepIndex(idx);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // 렌더 가드: stepIndex를 안전 범위로 클램핑해서 사용
  const maxIndex = steps.length - 1;
  const safeIndex = Math.max(0, Math.min(stepIndex, maxIndex));
  // 혹시라도 비정상 상태면(정말 드물지만) 리셋/로딩 처리
  if (!steps[safeIndex]) {
    setStepIndex(0);
    return <div className="h-screen flex items-center justify-center">로딩…</div>;
  }

  const CurrentStep = steps[stepIndex].Component;
  const { props = {} } = steps[stepIndex];
  const isStep5 = steps[stepIndex].Component === Step5;

  return (
    <div className="flex flex-col h-dvh">
      {stepIndex > 0 && (
        <HeaderIconLActionR
          title=""
          icon={stepIndex === indexOf(Step8) ? close : back}
          onIconClick={() => {
            if (steps[stepIndex].Component === Step7) {
              if (prevStepIndex != null) {
                setStepIndex(prevStepIndex);  // 직전 스텝으로 복귀 (4 또는 5)
              } else {
                setStepIndex(indexOf(Step4)); // 혹시 모를 예외용 fallback
              }
            } else if (steps[stepIndex].Component === Step8) {
              goToMain();
            } else {
              setStepIndex((prev) => Math.max(prev - 1, 0));
            }
          }}
          actionText={isStep5 ? "고객지원" : ""}  // Step4일 때만 텍스트
          onAction={
            isStep5
              ? () =>
                navigate("/ads/support", {
                  state: {
                    origin: "step5",
                    items: step5Items,
                    seed: step7Seed,
                  },
                })
              : undefined // 나머지 스텝에서는 액션 없음
          }
        />
      )}

      <div className="flex-grow min-h-0 relative overflow-y-auto overscroll-contain">
        <StepAnimator stepKey={stepIndex}>
          <CurrentStep {...props} onNext={goNext} goToStep={goToStep} />
        </StepAnimator>
      </div>
    </div>
  );
}
